import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  FlaskConical,
  Activity,
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  Calculator,
  Calendar,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Droplet,
  Droplets,
  Eye,
  FileCheck2,
  FileText,
  Gauge,
  Lightbulb,
  Lock,
  MapPin,
  Megaphone,
  PackageX,
  PauseCircle,
  Printer,
  Receipt,
  Scissors,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import { ApiError } from "../../lib/api";
import { hasPermission } from "../../lib/auth";
import { SemaforoDeuda, diasDesdeVencimiento } from "../../components/SemaforoDeuda";
import { CHART_COLORS } from "../../lib/chartColors";
import { pesos, pesosCortos } from "../../lib/format";
import {
  Badge,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  TableWrap,
} from "../../components/ui";
import { useToast } from "../../components/Toast";
import { useAuth } from "../auth/useAuth";
import { abrirOrden } from "../cortes/api";
import { fetchCicloConfig } from "../empresa/api";
import {
  fetchDashboardOperativo,
  type Alerta,
  type ItemPorHacer,
  type KpiCard,
  type Sugerencia,
  type TopDeudor,
} from "./api";
import { destinoDeFase, type DestinoFase } from "./atajos";
import { filasVisibles, MAX_BANDEJA } from "./bandeja";
import { MiniBarChart, MiniLineChart, Sparkline } from "./charts";
import {
  faseDelDia,
  fechaLarga,
  todasLasFases,
  type CicloFase,
  type CicloFaseInfo,
} from "./fases";

// KPI card plano (DESIGN §1/§8: sin gradientes ni sombras de color). El tono
// lo llevan el chip del ícono y el valor; el borde se tiñe sólo en alertas.
// `violet` se aliasa a `amber`: venía del backend para "atenciones
// abiertas" y no es un tono de la paleta.
const TONO_KPI_BG: Record<string, string> = {
  blue: "border-neutral-200 bg-white",
  amber: "border-amber-200 bg-white",
  emerald: "border-neutral-200 bg-white",
  rose: "border-rose-200 bg-white",
  violet: "border-amber-200 bg-white",
  slate: "border-neutral-200 bg-white",
};

const TONO_KPI_ICON: Record<string, string> = {
  blue: "bg-brand-100 text-brand-800 ring-1 ring-brand-200",
  amber: "bg-amber-100 text-amber-800 ring-1 ring-amber-200",
  emerald: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200",
  rose: "bg-rose-100 text-rose-800 ring-1 ring-rose-200",
  violet: "bg-amber-100 text-amber-800 ring-1 ring-amber-200",
  slate: "bg-neutral-100 text-neutral-600 ring-1 ring-neutral-200",
};

// Color del valor: tono 800 para contraste. `blue` queda en neutral-900, el
// "neutro institucional".
const TONO_KPI_VALUE: Record<string, string> = {
  blue: "text-neutral-900",
  amber: "text-amber-800",
  emerald: "text-emerald-800",
  rose: "text-rose-700",
  violet: "text-amber-800",
  slate: "text-neutral-600",
};

// Trazo de la sparkline según el tono del KPI (hex de `lib/chartColors`).
const SPARKLINE_STROKE: Record<string, string> = {
  blue: CHART_COLORS.brand600,
  emerald: CHART_COLORS.emerald600,
  amber: CHART_COLORS.amber600,
  rose: CHART_COLORS.rose600,
  violet: CHART_COLORS.amber600,
  slate: CHART_COLORS.neutral500,
};

const KPI_ICONS: Record<string, LucideIcon> = {
  contratos_activos: Users,
  cobertura_lecturas: Gauge,
  cobranza_mes: Wallet,
  deuda_total: Receipt,
  cortes_vigentes: Scissors,
  atenciones_abiertas: AlertCircle,
};

// KPI id → pantalla con el detalle.
const KPI_HREFS: Record<string, string> = {
  contratos_activos: "/contratos?estado=Activo",
  cobertura_lecturas: "/lecturas",
  cobranza_mes: "/caja",
  deuda_total: "/morosidad",
  cortes_vigentes: "/cortes",
  atenciones_abiertas: "/atenciones?estado=EnProceso",
};

// Íconos que el backend nombra en alertas, tareas y sugerencias.
const ICONOS: Record<string, LucideIcon> = {
  AlertOctagon,
  AlertCircle,
  AlertTriangle,
  Activity,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Droplet,
  Droplets,
  FileCheck2,
  FlaskConical,
  Gauge,
  Lightbulb,
  Lock,
  MapPin,
  Megaphone,
  PackageX,
  PauseCircle,
  Receipt,
  Scissors,
  TrendingUp,
  Wrench,
};

// ────────────────────────────────────────────────────────────────────
// Bandeja "Hoy" (PROP-02): alertas, tareas del ciclo y sugerencias en una
// sola lista con acción directa, ordenada por lo que más apura.
// ────────────────────────────────────────────────────────────────────

type NivelBandeja = Alerta["severidad"] | "tarea" | "revisar";

interface ItemBandeja {
  key: string;
  titulo: string;
  detalle: string | null;
  cantidad: number | null;
  href: string | null;
  accion: string | null;
  icono: LucideIcon;
  nivel: NivelBandeja;
}

// Orden: lo crítico y lo alto primero, después el trabajo del día, después
// lo que conviene mirar.
const ORDEN_NIVEL: Record<NivelBandeja, number> = {
  critica: 0,
  alta: 1,
  tarea: 2,
  media: 3,
  baja: 4,
  revisar: 5,
};

// El nivel va siempre con texto: el color no es el único portador.
const NIVEL_CHIP: Record<NivelBandeja, { texto: string; cls: string }> = {
  critica: { texto: "Crítica", cls: "bg-rose-100 text-rose-800 ring-1 ring-rose-200" },
  alta: { texto: "Alta", cls: "bg-amber-100 text-amber-800 ring-1 ring-amber-200" },
  tarea: { texto: "Del ciclo", cls: "bg-brand-100 text-brand-800" },
  media: { texto: "Media", cls: "bg-neutral-100 text-neutral-700" },
  baja: { texto: "Baja", cls: "bg-neutral-100 text-neutral-600" },
  revisar: { texto: "Revisar", cls: "bg-neutral-100 text-neutral-600" },
};

const ACCION_NIVEL: Record<NivelBandeja, string> = {
  critica: "Atender",
  alta: "Atender",
  tarea: "Hacer",
  media: "Ver",
  baja: "Ver",
  revisar: "Revisar",
};

function armarBandeja(
  alertas: Alerta[],
  porHacer: ItemPorHacer[],
  sugerencias: Sugerencia[],
): ItemBandeja[] {
  const items: ItemBandeja[] = [
    ...alertas.map((a) => ({
      key: `alerta-${a.id}`,
      titulo: a.titulo,
      detalle: a.detalle,
      cantidad: a.cantidad,
      href: a.href || null,
      accion: a.accion ?? null,
      icono: ICONOS[a.icono] ?? AlertTriangle,
      nivel: a.severidad,
    })),
    ...porHacer.map((t) => ({
      key: `tarea-${t.id}`,
      titulo: t.titulo,
      detalle: t.detalle,
      cantidad: t.cantidad,
      href: t.href || null,
      accion: t.accion ?? null,
      icono: ICONOS[t.icono] ?? Clock,
      nivel: "tarea" as const,
    })),
    // Las sugerencias son reglas de negocio con su pantalla (fugas, ceros,
    // reajuste, sectores atrasados): entran como "Revisar", al final.
    ...sugerencias.map((s) => ({
      key: `sugerencia-${s.id}`,
      titulo: s.titulo,
      detalle: s.detalle,
      cantidad: s.cantidad,
      href: s.href,
      accion: s.accion ?? null,
      icono: ICONOS[s.icono] ?? Lightbulb,
      nivel: "revisar" as const,
    })),
  ];
  // sort estable: dentro de un nivel se respeta el orden del backend.
  return items.sort((a, b) => ORDEN_NIVEL[a.nivel] - ORDEN_NIVEL[b.nivel]);
}

export function DashboardPage(): JSX.Element {
  const { state } = useAuth();
  const ahora = useMemo(() => new Date(), []);

  const puedeConfigurar = hasPermission(state, "empresa.configurar");
  const cfgQ = useQuery({
    queryKey: ["empresa", "ciclo-config"],
    queryFn: fetchCicloConfig,
    enabled: puedeConfigurar,
    staleTime: 5 * 60 * 1000,
  });

  const dashQ = useQuery({
    queryKey: ["dashboard", "operativo"],
    queryFn: fetchDashboardOperativo,
    staleTime: 60 * 1000, // 1 min — KPIs no cambian segundo a segundo
  });

  const fase = useMemo(
    () => faseDelDia(ahora, cfgQ.data),
    [ahora, cfgQ.data],
  );
  const fases = useMemo(() => todasLasFases(cfgQ.data), [cfgQ.data]);

  const destino = useMemo(
    () =>
      destinoDeFase(fase.id, {
        hayCierrePorFacturar:
          dashQ.data?.por_hacer_hoy.some((i) => i.id === "cierre_facturar") ??
          false,
        tienePermiso: (p) => hasPermission(state, p),
      }),
    [fase.id, dashQ.data?.por_hacer_hoy, state],
  );

  // Sparkline por id estable del backend (`kpi_id` de la serie).
  const sparklineByKpiId = useMemo<Record<string, number[]>>(() => {
    const map: Record<string, number[]> = {};
    for (const serie of dashQ.data?.tendencias ?? []) {
      if (serie.kpi_id) map[serie.kpi_id] = serie.puntos.map((p) => p.valor);
    }
    return map;
  }, [dashQ.data?.tendencias]);

  const bandeja = useMemo(
    () =>
      dashQ.data
        ? armarBandeja(
            dashQ.data.alertas,
            dashQ.data.por_hacer_hoy,
            dashQ.data.sugerencias,
          )
        : [],
    [dashQ.data],
  );

  return (
    <div className="space-y-6">
      {/* ── Header con fecha + fase ──────────────────────────────── */}
      <header>
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <Calendar className="h-3.5 w-3.5" />
          <span>{fechaLarga(ahora)}</span>
          {dashQ.data && (
            <>
              <span>·</span>
              <span>Periodo {dashQ.data.periodo}</span>
            </>
          )}
        </div>
        <div className="mt-1 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-neutral-900">Inicio</h1>
            {/* Hora real del dato: se cachea un minuto, no es "en vivo". */}
            {dashQ.dataUpdatedAt > 0 && (
              <span className="text-xs text-neutral-500 tabular-nums">
                Actualizado{" "}
                {new Date(dashQ.dataUpdatedAt).toLocaleTimeString("es-CL", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            )}
          </div>
          <PhasePill fase={fase} dia={ahora.getDate()} destino={destino} />
        </div>
      </header>

      {dashQ.isPending && <Cargando texto="Cargando indicadores…" />}

      {dashQ.isError && (
        <ErrorCarga
          error={dashQ.error}
          mensaje="No se pudieron cargar los indicadores."
          onReintentar={() => void dashQ.refetch()}
        />
      )}

      {dashQ.data && (
        <>
          {/* ── 1 · Hoy: lo que necesita acción (va primero) ───────── */}
          <BandejaHoy items={bandeja} />

          {/* ── 2 · Estado actual (KPIs) ────────────────────────── */}
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-neutral-600 mb-2">
              Estado actual
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {dashQ.data.kpis.map((k) => (
                <KpiCardView
                  key={k.id}
                  kpi={k}
                  sparkline={sparklineByKpiId[k.id]}
                />
              ))}
            </div>
          </section>

          {/* ── 3 · Ciclo del mes ───────────────────────────────── */}
          <section>
            <CycleTimeline
              fases={fases}
              faseActiva={fase}
              dia={ahora.getDate()}
              destino={destino}
            />
          </section>

          {/* ── 4 · Tendencias ──────────────────────────────────── */}
          {dashQ.data.tendencias.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-neutral-600 mb-2">
                Tendencia últimos 6 meses
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {dashQ.data.tendencias.map((serie) => (
                  <div
                    key={serie.kpi_id ?? serie.titulo}
                    className="rounded-lg ring-1 ring-neutral-200 bg-white p-4"
                  >
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-neutral-800">
                        {serie.titulo}
                      </h3>
                      {serie.descripcion && (
                        <p className="text-xs text-neutral-500">
                          {serie.descripcion}
                        </p>
                      )}
                    </div>
                    <div className="mt-3">
                      {/* Montos en barras; porcentajes en línea. */}
                      {serie.unidad === "$" ? (
                        <MiniBarChart
                          puntos={serie.puntos}
                          titulo={serie.titulo}
                          formatValor={pesosCortos}
                        />
                      ) : (
                        <MiniLineChart
                          puntos={serie.puntos}
                          titulo={serie.titulo}
                          formatValor={(v) => `${v}${serie.unidad}`}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── 5 · Top deudores ────────────────────────────────── */}
          {dashQ.data.top_deudores.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-neutral-600 mb-2 flex items-center gap-2">
                <TrendingDown className="h-3.5 w-3.5" />
                Top 10 deudores
                <Link
                  to="/morosidad"
                  className="ml-auto inline-flex items-center gap-1 text-xs font-medium normal-case tracking-normal text-brand-700 hover:text-brand-800"
                >
                  Ver todos los deudores
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </h2>
              <TableWrap label="Deudores principales">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-600">
                    <tr>
                      <th scope="col" className="px-4 py-2.5 text-left font-semibold w-10">#</th>
                      <th scope="col" className="px-4 py-2.5 text-left font-semibold">Socio</th>
                      <th scope="col" className="px-4 py-2.5 text-left font-semibold">Sector</th>
                      <th scope="col" className="px-4 py-2.5 text-right font-semibold">Boletas</th>
                      <th scope="col" className="px-4 py-2.5 text-right font-semibold">Deuda</th>
                      <th scope="col" className="px-4 py-2.5 text-left font-semibold">Antigüedad</th>
                      <th scope="col" className="px-4 py-2.5 text-right font-semibold w-20">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {dashQ.data.top_deudores.map((d, i) => (
                      <DeudorRow key={d.socio_id} index={i} deudor={d} />
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            </section>
          )}
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Componentes auxiliares
// ────────────────────────────────────────────────────────────────────

function BandejaHoy({ items }: { items: ItemBandeja[] }): JSX.Element {
  const [expandida, setExpandida] = useState(false);
  const n = items.length;
  const visibles = filasVisibles(items, expandida);
  const ocultas = n - visibles.length;
  return (
    <section aria-labelledby="bandeja-hoy">
      {/* Cada fila es un asunto (un grupo de casos), no un registro: el
          número de casos va en el distintivo de la fila (auditoría UX-12). */}
      <h2
        id="bandeja-hoy"
        className="mb-2 flex items-center gap-2 text-base font-semibold text-neutral-900"
      >
        <Clock className="h-4 w-4 text-brand-700" aria-hidden />
        {n === 0
          ? "Hoy: sin pendientes"
          : `Hoy: ${n} ${n === 1 ? "asunto por revisar" : "asuntos por revisar"}`}
      </h2>
      {n === 0 ? (
        <div className="rounded-lg ring-1 ring-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          Sin alertas ni tareas del ciclo para hoy.
        </div>
      ) : (
        <>
          <ul className="rounded-xl ring-1 ring-neutral-200 bg-white divide-y divide-neutral-100 overflow-hidden">
            {visibles.map((it) => (
              <li key={it.key}>
                <FilaBandeja item={it} />
              </li>
            ))}
          </ul>
          {n > MAX_BANDEJA && (ocultas > 0 || expandida) && (
            <button
              type="button"
              onClick={() => setExpandida((v) => !v)}
              aria-expanded={expandida}
              className="mt-2 inline-flex min-h-9 items-center rounded text-sm font-medium text-brand-700 hover:text-brand-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 [@media(pointer:coarse)]:min-h-11"
            >
              {expandida ? "Ver menos" : `Ver todos (${ocultas} más)`}
            </button>
          )}
        </>
      )}
    </section>
  );
}

function FilaBandeja({ item }: { item: ItemBandeja }): JSX.Element {
  const Icon = item.icono;
  const chip = NIVEL_CHIP[item.nivel];
  const accion = item.accion ?? ACCION_NIVEL[item.nivel];
  const casos =
    item.cantidad != null
      ? `${item.cantidad.toLocaleString("es-CL")} ${item.cantidad === 1 ? "caso" : "casos"}`
      : null;
  const cuerpo = (
    <>
      <span
        className={`inline-flex w-16 shrink-0 justify-center rounded-md px-1.5 py-0.5 text-xs font-semibold ${chip.cls}`}
      >
        {chip.texto}
      </span>
      <Icon className="hidden h-4 w-4 shrink-0 text-neutral-500 sm:block" aria-hidden />
      <div className="min-w-0 flex-1 leading-tight">
        {/* El título se lee completo (puede ocupar dos líneas); el detalle
            se corta en dos líneas y queda entero en el title. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-neutral-900">{item.titulo}</span>
          {item.cantidad != null && (
            <span
              className="inline-flex min-w-6 items-center justify-center rounded-full bg-neutral-900 px-1.5 text-xs font-semibold tabular-nums text-white"
              title={casos ?? undefined}
            >
              {item.cantidad.toLocaleString("es-CL")}
            </span>
          )}
        </div>
        {item.detalle && (
          <div className="mt-0.5 line-clamp-2 text-xs text-neutral-600" title={item.detalle}>
            {item.detalle}
          </div>
        )}
      </div>
      {item.href && (
        <span className="inline-flex shrink-0 items-center gap-0.5 text-sm font-medium text-brand-700 group-hover:text-brand-800">
          {/* En teléfono queda sólo la flecha; el nombre accesible del link
              dice la acción completa. */}
          <span className="hidden sm:inline">{accion}</span>
          <ChevronRight className="h-4 w-4" aria-hidden />
        </span>
      )}
    </>
  );
  const cls = "group flex items-center gap-3 px-4 py-2.5";
  if (!item.href) return <div className={cls}>{cuerpo}</div>;
  return (
    <Link
      to={item.href}
      aria-label={`${accion}: ${item.titulo}${casos ? ` (${casos})` : ""}`}
      className={`${cls} hover:bg-neutral-50 transition-colors`}
    >
      {cuerpo}
    </Link>
  );
}

function formatKpi(k: KpiCard): string {
  if (k.es_porcentaje) return `${k.valor}%`;
  if (k.es_monto) return pesos(k.valor);
  return k.valor.toLocaleString("es-CL");
}

function KpiCardView({
  kpi,
  sparkline,
}: {
  kpi: KpiCard;
  sparkline?: number[] | undefined;
}): JSX.Element {
  const Icon = KPI_ICONS[kpi.id] ?? Activity;
  const bgCls = TONO_KPI_BG[kpi.tono] ?? TONO_KPI_BG.slate;
  const iconCls = TONO_KPI_ICON[kpi.tono] ?? TONO_KPI_ICON.slate;
  const valueCls = TONO_KPI_VALUE[kpi.tono] ?? TONO_KPI_VALUE.slate;
  const valorStr = formatKpi(kpi);
  const href = KPI_HREFS[kpi.id];

  let DeltaIcon = TrendingUp;
  let deltaCls = "text-emerald-700";
  if (kpi.delta_vs_anterior != null) {
    if (kpi.delta_vs_anterior < 0) {
      DeltaIcon = TrendingDown;
      deltaCls = "text-rose-700";
    } else if (kpi.delta_vs_anterior === 0) {
      deltaCls = "text-neutral-600";
    }
  }

  const hasSparkline = sparkline && sparkline.length >= 2;
  const sparkStroke = SPARKLINE_STROKE[kpi.tono] ?? SPARKLINE_STROKE.slate;

  const cardCls = `group relative overflow-hidden rounded-xl border ${bgCls} ${hasSparkline ? "pb-7" : ""} px-3.5 py-3 transition-colors duration-150 ${href ? "cursor-pointer hover:border-brand-300" : ""}`;

  const inner = (
    <>
      {hasSparkline && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-7"
          aria-hidden
        >
          <Sparkline
            values={sparkline!}
            stroke={sparkStroke}
            className="h-full w-full"
          />
        </div>
      )}

      <div className="relative z-10 flex items-center gap-3">
        <span
          className={`grid place-items-center h-9 w-9 rounded-lg shrink-0 ${iconCls}`}
        >
          <Icon className="h-4 w-4" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-neutral-600 leading-tight truncate">
            {kpi.titulo}
          </div>
          <div
            className={`mt-0.5 text-[24px] font-bold tracking-tight tabular-nums leading-none ${valueCls}`}
          >
            {valorStr}
          </div>
          {/* Contra qué se mide el número (lo arma el backend). */}
          {kpi.sublabel && (
            <div className="mt-0.5 text-xs text-neutral-600 truncate" title={kpi.sublabel}>
              {kpi.sublabel}
            </div>
          )}
          {kpi.delta_vs_anterior != null && kpi.delta_label && (
            <div
              className={`mt-0.5 inline-flex items-center gap-1 text-xs font-semibold ${deltaCls}`}
            >
              <DeltaIcon className="h-3 w-3" strokeWidth={2.5} aria-hidden />
              <span>
                {kpi.delta_vs_anterior > 0 ? "+" : ""}
                {kpi.delta_vs_anterior}
                {kpi.es_porcentaje ? " pts" : ""} {kpi.delta_label}
              </span>
            </div>
          )}
        </div>
      </div>

      {href && (
        <ChevronRight
          className="absolute top-2.5 right-2.5 h-3.5 w-3.5 text-neutral-400 opacity-0 group-hover:opacity-100 group-hover:text-brand-700 transition-opacity z-20"
          aria-hidden
        />
      )}
    </>
  );

  if (href) {
    return (
      <Link to={href} className={cardCls} aria-label={`Ver ${kpi.titulo}`}>
        {inner}
      </Link>
    );
  }
  return <div className={cardCls}>{inner}</div>;
}

// Formatea antigüedad en meses → texto legible.
// 0 → "Este mes" (deuda del periodo actual sin pagar todavía).
// 1 → "1 mes". 2-11 → "N meses". 12+ → "X años".


const CORTE_LABEL: Record<string, string> = {
  Pendiente: "Orden abierta",
  Notificada: "Notificada",
  EnRuta: "En ruta",
  Cortada: "Cortada",
};

// NAV-07: la cuenta corriente vuelve al Inicio con su miga de pan.
const DESDE_INICIO = { desde: { etiqueta: "Inicio", ruta: "/" } };

function DeudorRow({
  index,
  deudor,
}: {
  index: number;
  deudor: TopDeudor;
}): JSX.Element {
  const { state } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [confirmar, setConfirmar] = useState(false);
  // El corte es por contrato: sólo se ofrece si el socio tiene un único
  // contrato moroso (contrato_id != null), NO tiene ya un corte en proceso, y
  // el usuario tiene permiso de gestión de cortes. Con varios contratos se
  // gestiona desde la ficha del socio.
  const puedeCortar =
    deudor.contrato_id != null &&
    deudor.estado_corte == null &&
    hasPermission(state, "cortes.gestionar");

  const abrir = useMutation({
    mutationFn: () =>
      abrirOrden({
        contrato_id: deudor.contrato_id as number,
        monto_deuda_al_corte: deudor.monto_deuda,
      }),
    onSuccess: () => {
      toast.success(`Orden de corte abierta para ${deudor.socio_nombre}`);
      setConfirmar(false);
      void qc.invalidateQueries({ queryKey: ["dashboard", "operativo"] });
    },
    onError: (err) =>
      toast.error(
        err instanceof ApiError ? err.message : "No se pudo abrir la orden de corte",
      ),
  });


  return (
    <tr className="hover:bg-neutral-50 transition-colors">
      <td className="px-4 py-2.5 text-neutral-500 tabular-nums">{index + 1}</td>
      <td className="px-4 py-2.5">
        <div className="font-medium text-neutral-900 leading-tight">
          {deudor.socio_nombre}
        </div>
        {deudor.socio_numero && (
          <div className="text-xs text-neutral-500 mt-0.5">
            N° {deudor.socio_numero}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5 text-neutral-700">
        {deudor.sector_nombre ?? (
          <span className="text-neutral-500">—</span>
        )}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
        {deudor.boletas_vencidas}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-rose-700">
        {pesos(deudor.monto_deuda)}
      </td>
      <td className="px-4 py-2.5">
        <SemaforoDeuda
          meses={deudor.antiguedad_meses}
          dias={diasDesdeVencimiento([deudor.vencimiento_mas_antiguo])}
          monto={deudor.monto_deuda}
          mostrarMonto={false}
        />
      </td>
      <td className="px-4 py-2.5 text-right">
        <div className="inline-flex items-center gap-1">
          <Link
            to={`/socios/${deudor.socio_id}/cuenta-corriente`}
            state={DESDE_INICIO}
            className="grid place-items-center h-7 w-7 rounded-md text-neutral-500 hover:bg-brand-50 hover:text-brand-700 transition"
            title="Ver cuenta corriente"
            aria-label={`Ver cuenta corriente de ${deudor.socio_nombre}`}
          >
            <Eye className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
          <Link
            to={`/socios/${deudor.socio_id}/cuenta-corriente?accion=imprimir`}
            state={DESDE_INICIO}
            className="grid place-items-center h-7 w-7 rounded-md text-neutral-500 hover:bg-brand-50 hover:text-brand-700 transition"
            title="Imprimir boleta"
            aria-label={`Imprimir boleta de ${deudor.socio_nombre}`}
          >
            <Printer className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
          {deudor.estado_corte != null && (
            <span title={`Ya tiene una orden de corte activa (${deudor.estado_corte})`}>
              <Badge tone={deudor.estado_corte === "Cortada" ? "rose" : "amber"}>
                <Scissors className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
                {CORTE_LABEL[deudor.estado_corte] ?? deudor.estado_corte}
              </Badge>
            </span>
          )}
          {puedeCortar && (
            <button
              type="button"
              onClick={() => setConfirmar(true)}
              className="grid place-items-center h-7 w-7 rounded-md text-neutral-500 hover:bg-rose-50 hover:text-rose-700 transition"
              title="Abrir orden de corte"
              aria-label={`Abrir orden de corte para ${deudor.socio_nombre}`}
            >
              <Scissors className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          )}
        </div>
        <ConfirmDialog
          open={confirmar}
          title="Abrir orden de corte"
          tone="danger"
          confirmLabel="Abrir corte"
          loading={abrir.isPending}
          onCancel={() => setConfirmar(false)}
          onConfirm={() => abrir.mutate()}
          description={
            <>
              Se abrirá una orden de corte para{" "}
              <strong>{deudor.socio_nombre}</strong> por una deuda de{" "}
              <strong>{pesos(deudor.monto_deuda)}</strong>. Queda en estado{" "}
              <strong>Pendiente</strong>: todavía no corta el suministro — después
              la notificas, programas y ejecutas desde Cortes.
            </>
          }
        />
      </td>
    </tr>
  );
}

// ────────────────────────────────────────────────────────────────────
// PhasePill — chip de la fase actual en el encabezado. Enlaza a la
// pantalla donde se hace el trabajo de la fase (NAV-02).
// ────────────────────────────────────────────────────────────────────

function PhasePill({
  fase,
  dia,
  destino,
}: {
  fase: CicloFaseInfo;
  dia: number;
  destino: DestinoFase | null;
}): JSX.Element {
  const Icon = FASE_ICONS[fase.id];
  // Progreso dentro de la fase actual (día N de span). Cíclico: la fase
  // puede cruzar el fin de mes, así que se cuenta hacia adelante en módulo 31.
  const diasEnFase = Math.min(fase.span, ((dia - fase.diasInicio + 31) % 31) + 1);
  const progressPct = Math.min(100, (diasEnFase / fase.span) * 100);

  const cuerpo = (
    <>
      <span className="grid place-items-center h-9 w-9 rounded-lg bg-brand-50 text-brand-800 shrink-0">
        <Icon className="h-4 w-4" strokeWidth={2} />
      </span>
      <div className="leading-tight">
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-600">
          Fase
        </div>
        <div className="text-[13px] font-semibold text-neutral-900 mt-0.5">
          {fase.titulo}
        </div>
      </div>
      <div className="h-1 w-16 rounded-full bg-neutral-100 overflow-hidden" aria-hidden>
        <div
          className="h-full rounded-full bg-brand-600"
          style={{ width: `${progressPct}%` }}
        />
      </div>
      <div className="text-xs font-semibold text-brand-800 tabular-nums whitespace-nowrap">
        Día {diasEnFase} / {fase.span}
      </div>
      {destino && (
        <ChevronRight className="h-4 w-4 text-brand-700" aria-hidden />
      )}
    </>
  );

  const cls =
    "inline-flex items-center gap-3 rounded-xl bg-white px-3.5 py-2 ring-1 ring-neutral-200";
  if (!destino) return <div className={cls}>{cuerpo}</div>;
  return (
    <Link
      to={destino.to}
      className={`${cls} hover:ring-brand-300 transition-colors`}
      title={destino.etiqueta}
      aria-label={`Fase ${fase.titulo}, día ${diasEnFase} de ${fase.span}: ${destino.etiqueta}`}
    >
      {cuerpo}
    </Link>
  );
}

// ────────────────────────────────────────────────────────────────────
// CycleTimeline — barra horizontal con las 5 fases del mes y la
// posición del día actual. El segmento activo y la descripción de la
// fase enlazan a su pantalla.
// ────────────────────────────────────────────────────────────────────

const FASE_ICONS: Record<CicloFase, LucideIcon> = {
  lecturas: Gauge,
  cierre: Calculator,
  emision: FileText,
  cobranza: Wallet,
  morosidad: Scissors,
};

const FASE_SEG_INACTIVE: Record<CicloFaseInfo["tono"], string> = {
  blue: "bg-brand-100 text-brand-900",
  amber: "bg-amber-100 text-amber-900",
  slate: "bg-neutral-100 text-neutral-800",
  emerald: "bg-emerald-100 text-emerald-900",
  rose: "bg-rose-100 text-rose-900",
};

// Activo: un paso más de tono + borde inferior grueso (borde, no sombra).
const FASE_SEG_ACTIVE: Record<CicloFaseInfo["tono"], string> = {
  blue: "bg-brand-200 text-brand-900 ring-1 ring-inset ring-brand-400 border-b-4 border-brand-600",
  amber: "bg-amber-200 text-amber-900 ring-1 ring-inset ring-amber-400 border-b-4 border-amber-500",
  slate: "bg-brand-100 text-brand-900 ring-1 ring-inset ring-brand-400 border-b-4 border-brand-600",
  emerald:
    "bg-emerald-200 text-emerald-900 ring-1 ring-inset ring-emerald-400 border-b-4 border-emerald-500",
  rose: "bg-rose-200 text-rose-900 ring-1 ring-inset ring-rose-400 border-b-4 border-rose-500",
};

const FASE_DIVIDER: Record<CicloFaseInfo["tono"], string> = {
  blue: "bg-brand-200/60",
  amber: "bg-amber-200/60",
  slate: "bg-neutral-300/60",
  emerald: "bg-emerald-200/60",
  rose: "bg-rose-200/60",
};

function CycleTimeline({
  fases,
  faseActiva,
  dia,
  destino,
}: {
  fases: ReadonlyArray<CicloFaseInfo>;
  faseActiva: CicloFaseInfo;
  dia: number;
  destino: DestinoFase | null;
}): JSX.Element {
  // Canónico: 31 días por mes. En meses cortos hay una pequeña distorsión
  // de los últimos días — aceptable para una visualización indicativa.
  const TOTAL = 31;
  // Posición del marcador "hoy" RELATIVA al ciclo, no al día absoluto: se
  // acumula el ancho de las fases previas a la activa + el avance en ella.
  let offsetDias = 0;
  for (const f of fases) {
    if (f.id === faseActiva.id) break;
    offsetDias += f.span;
  }
  const avanceEnFase = Math.min(
    faseActiva.span,
    ((dia - faseActiva.diasInicio + TOTAL) % TOTAL) + 1,
  );
  const diaPct = ((offsetDias + avanceEnFase - 0.5) / TOTAL) * 100;

  const FaseActivaIcon = FASE_ICONS[faseActiva.id];

  return (
    <div className="rounded-xl ring-1 ring-neutral-200 bg-white p-5">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-600">
        Ciclo del mes
      </h3>

      <div className="relative">
        <div className="flex h-16 rounded-lg overflow-hidden ring-1 ring-neutral-200">
          {fases.map((f, idx) => {
            const widthPct = (f.span / TOTAL) * 100;
            const isActive = f.id === faseActiva.id;
            const cls = isActive
              ? FASE_SEG_ACTIVE[f.tono]
              : FASE_SEG_INACTIVE[f.tono];
            const Icon = FASE_ICONS[f.id];
            const contenido = (
              <>
                <div className="flex items-center gap-1.5 max-w-full">
                  <Icon
                    className={`h-3.5 w-3.5 shrink-0 ${isActive ? "" : "opacity-60"}`}
                    strokeWidth={isActive ? 2.25 : 1.75}
                  />
                  <span
                    className={`text-xs leading-tight truncate ${isActive ? "font-semibold" : "font-medium"}`}
                  >
                    {f.titulo}
                  </span>
                </div>
                <span className="font-mono text-xs tabular-nums">
                  {f.diasInicio}–{f.diasFin}
                </span>
                {idx < fases.length - 1 && (
                  <span
                    className={`absolute right-0 top-2 bottom-2 w-px ${FASE_DIVIDER[f.tono]}`}
                    aria-hidden
                  />
                )}
              </>
            );
            const segCls = `relative flex flex-col items-center justify-center gap-0.5 px-1 ${cls}`;
            const titulo = `${f.titulo} (días ${f.diasInicio}–${f.diasFin})`;
            if (isActive && destino) {
              return (
                <Link
                  key={f.id}
                  to={destino.to}
                  className={`${segCls} hover:brightness-95`}
                  style={{ width: `${widthPct}%` }}
                  title={`${titulo}: ${destino.etiqueta}`}
                >
                  {contenido}
                </Link>
              );
            }
            return (
              <div
                key={f.id}
                className={segCls}
                style={{ width: `${widthPct}%` }}
                title={titulo}
              >
                {contenido}
              </div>
            );
          })}
        </div>

        {/* Marcador del día actual, sin animación (DESIGN §8). */}
        <div
          className="pointer-events-none absolute top-0 bottom-0 z-10"
          style={{ left: `${diaPct}%` }}
          aria-hidden
        >
          <div className="absolute top-0 bottom-0 -left-[1.5px] w-[3px] bg-brand-900 rounded-full" />
          <span className="absolute -top-2 -left-2 inline-flex h-4 w-4 rounded-full bg-brand-900 ring-2 ring-white" />
          <div className="absolute -bottom-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-brand-900 px-2.5 py-1 text-xs font-bold text-white tabular-nums">
            hoy día {dia}
          </div>
        </div>
      </div>

      <div className="mt-14 flex justify-between font-mono text-xs font-semibold text-neutral-700 tabular-nums px-0.5">
        <span>1</span>
        <span>5</span>
        <span>10</span>
        <span>15</span>
        <span>20</span>
        <span>25</span>
        <span>31</span>
      </div>

      {/* Descripción de la fase activa + a dónde ir. */}
      <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-neutral-50 px-3 py-2.5 ring-1 ring-neutral-100">
        <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-md bg-white ring-1 ring-neutral-200 shrink-0">
          <FaseActivaIcon className="h-3.5 w-3.5 text-brand-700" strokeWidth={2} />
        </span>
        <p className="flex-1 text-xs text-neutral-600 leading-relaxed">
          <span className="font-semibold text-neutral-900">
            {faseActiva.titulo}
          </span>{" "}
          — {faseActiva.descripcion}
        </p>
        {destino && (
          <Link
            to={destino.to}
            className="inline-flex shrink-0 items-center gap-0.5 self-center text-sm font-medium text-brand-700 hover:text-brand-800"
          >
            {destino.etiqueta}
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Link>
        )}
      </div>
    </div>
  );
}
